import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

type ListingPhotoGalleryModalProps = {
  photos: string[]
  initialIndex?: number
  isOpen: boolean
  onClose: () => void
}

const ListingPhotoGalleryModal = ({
  photos,
  initialIndex = 0,
  isOpen,
  onClose,
}: ListingPhotoGalleryModalProps) => {
  const { t } = useTranslation()
  const [index, setIndex] = useState(initialIndex)

  useEffect(() => {
    if (isOpen) setIndex(initialIndex)
  }, [isOpen, initialIndex])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setIndex(i => (i > 0 ? i - 1 : photos.length - 1))
      if (e.key === 'ArrowRight') setIndex(i => (i < photos.length - 1 ? i + 1 : 0))
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose, photos.length])

  const goPrev = useCallback(() => {
    setIndex(i => (i > 0 ? i - 1 : photos.length - 1))
  }, [photos.length])

  const goNext = useCallback(() => {
    setIndex(i => (i < photos.length - 1 ? i + 1 : 0))
  }, [photos.length])

  if (!isOpen || photos.length === 0) return null

  const current = photos[index] ?? photos[0]

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('createAd.flow.publication.preview.galleryTitle')}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        aria-label={t('createAd.flow.publication.preview.galleryClose')}
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {photos.length > 1 ? (
        <>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              goPrev()
            }}
            className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-6"
            aria-label={t('createAd.flow.publication.preview.galleryPrev')}
          >
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              goNext()
            }}
            className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
            aria-label={t('createAd.flow.publication.preview.galleryNext')}
          >
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      ) : null}

      <div
        className="relative flex max-h-[85vh] w-full max-w-5xl flex-col items-center"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={current}
          alt=""
          className="max-h-[78vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
        />
        {photos.length > 1 ? (
          <p className="mt-4 text-sm font-medium text-white/90 tabular-nums">
            {t('createAd.flow.publication.preview.galleryCounter', {
              current: index + 1,
              total: photos.length,
            })}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

export default ListingPhotoGalleryModal
